import React, { useEffect, useState } from 'react';
import api from '../api';
import { Search, UserPlus, X, Shield, ShieldOff, RotateCw, Building2 } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils';

export default function AdminUsers() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', company_id: '', role: 'client' });
  const [inviteLink, setInviteLink] = useState('');

  useEffect(() => { loadData(); // eslint-disable-next-line
  }, [companyFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = companyFilter ? `?company_id=${companyFilter}` : '';
      const [usersRes, companiesRes] = await Promise.all([
        api.get(`/api/admin/users${params}`),
        api.get('/api/admin/companies?limit=200')
      ]);
      setUsers(usersRes.data.users);
      setCompanies(companiesRes.data.companies);
    } catch (err) { console.error(err); toast.error('Kunne ikke laste brukerliste'); }
    finally { setLoading(false); }
  };

  const toggleActive = async (userId, currentActive) => {
    try {
      await api.put(`/api/admin/users/${userId}`, { active: !currentActive });
      toast.success(currentActive ? 'Bruker deaktivert' : 'Bruker aktivert');
      loadData();
    } catch (err) { console.error(err); toast.error('Kunne ikke oppdatere bruker'); }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await api.put(`/api/admin/users/${userId}`, { role: newRole });
      toast.success('Rolle oppdatert');
      loadData();
    } catch (err) { console.error(err); toast.error('Kunne ikke endre rolle'); }
  };

  const handleInvite = async () => {
    try {
      const { data } = await api.post('/api/admin/invite', inviteForm);
      setInviteLink(data.invite_url);
    } catch (err) { console.error(err); toast.error('Kunne ikke opprette invitasjon'); }
  };

  const resendInvite = async (userId) => {
    try {
      const { data } = await api.post(`/api/admin/users/${userId}/resend-invite`);
      try { await navigator.clipboard.writeText(data.invite_url); } catch (e) {}
      toast.success('Invitasjonslenke kopiert til utklippstavlen');
    } catch (err) { console.error(err); toast.error('Kunne ikke generere invitasjonslenke'); }
  };

  const filtered = users.filter(u =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">Brukere</h1>
          <p className="text-slate-500 text-sm mt-1">{users.length} brukere på tvers av alle bedrifter</p>
        </div>
        <button onClick={() => setShowInvite(true)} data-testid="invite-new-user-button"
          className="flex items-center gap-2 px-4 py-2.5 bg-accent-blue hover:bg-accent-blue-hover text-white font-medium rounded-lg transition-colors text-sm">
          <UserPlus size={16} /> Inviter bruker
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input type="text" placeholder="Søk etter navn eller e-post..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue" />
        </div>
        <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}
          className="px-3 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue">
          <option value="">Alle bedrifter</option>
          {companies.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
      </div>

      <div className="bg-navy-800 border border-navy-600 rounded-lg overflow-hidden">
        <table className="w-full" data-testid="admin-users-table">
          <thead>
            <tr className="border-b border-navy-600">
              <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Navn</th>
              <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">E-post</th>
              <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Bedrift</th>
              <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Rolle</th>
              <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Siste innlogging</th>
              <th className="text-left px-4 py-3 text-xs font-semibold tracking-widest uppercase text-slate-500">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {loading ? [...Array(4)].map((_, i) => (
              <tr key={i} className="animate-pulse">{[...Array(7)].map((_, j) => <td key={j} className="px-4 py-4"><div className="h-4 bg-navy-700 rounded w-20" /></td>)}</tr>
            )) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-slate-500 text-sm">Ingen brukere funnet{search ? ` for «${search}»` : ''}</td></tr>
            ) : filtered.map(u => (
              <tr key={u._id} className="border-b border-navy-600/50 hover:bg-navy-700/50 transition-colors">
                <td className="px-4 py-3.5 text-sm font-medium text-white">{u.name}</td>
                <td className="px-4 py-3.5 text-sm text-slate-400">{u.email}</td>
                <td className="px-4 py-3.5">
                  {u.role !== 'admin' ? (
                    <div className="flex items-center gap-1.5">
                      <Building2 size={12} className="text-slate-500" />
                      <span className="text-xs text-accent-blue">{u.company_name || '—'}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-500">—</span>
                  )}
                </td>
                <td className="px-4 py-3.5">
                  <select value={u.role} onChange={(e) => changeRole(u._id, e.target.value)}
                    className="text-xs px-2 py-1 bg-navy-700 border border-navy-600 rounded text-slate-300 focus:outline-none focus:ring-1 focus:ring-accent-blue">
                    <option value="client">Klient</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td className="px-4 py-3.5">
                  <span className={`text-xs px-2 py-1 rounded ${u.active !== false ? 'bg-accent-green/15 text-accent-green' : 'bg-accent-red/15 text-accent-red'}`}>
                    {u.active !== false ? 'Aktiv' : 'Deaktivert'}
                  </span>
                </td>
                <td className="px-4 py-3.5 text-xs text-slate-500">
                  {u.last_login ? formatDate(u.last_login) : <span className="italic">Aldri</span>}
                </td>
                <td className="px-4 py-3.5">
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleActive(u._id, u.active !== false)} title={u.active !== false ? 'Deaktiver' : 'Aktiver'}
                      className="p-1.5 rounded text-slate-500 hover:text-white hover:bg-navy-600 transition-colors">
                      {u.active !== false ? <ShieldOff size={14} /> : <Shield size={14} />}
                    </button>
                    <button onClick={() => resendInvite(u._id)} title="Send ny invitasjon"
                      className="p-1.5 rounded text-slate-500 hover:text-accent-blue hover:bg-navy-600 transition-colors">
                      <RotateCw size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showInvite && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-navy-800 border border-navy-600 rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-lg font-semibold text-white">Inviter ny bruker</h2>
              <button onClick={() => { setShowInvite(false); setInviteLink(''); }} className="text-slate-500 hover:text-white"><X size={18} /></button>
            </div>
            {!inviteLink ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">E-post</label>
                  <input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                    className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-accent-blue" placeholder="bruker@bedrift.no" />
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Bedrift</label>
                  <select value={inviteForm.company_id} onChange={(e) => setInviteForm({ ...inviteForm, company_id: e.target.value })}
                    className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue">
                    <option value="">Velg bedrift</option>
                    {companies.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-widest uppercase text-slate-500 mb-2">Rolle</label>
                  <select value={inviteForm.role} onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                    className="w-full px-4 py-3 bg-navy-900 border border-navy-600 rounded-lg text-sm text-white focus:outline-none focus:ring-2 focus:ring-accent-blue">
                    <option value="client">Klient</option>
                    <option value="manager">Manager</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <button onClick={handleInvite} disabled={!inviteForm.email || !inviteForm.company_id}
                  className="w-full py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors disabled:opacity-50">
                  Generer invitasjonslenke
                </button>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-400 mb-3">Del denne lenken med brukeren:</p>
                <input readOnly value={inviteLink}
                  className="w-full px-3 py-2.5 bg-navy-900 border border-navy-600 rounded-lg text-xs text-slate-300 font-mono" />
                <button onClick={() => { navigator.clipboard.writeText(inviteLink); }}
                  className="mt-3 w-full py-2.5 bg-accent-green/20 text-accent-green font-medium rounded-lg text-sm hover:bg-accent-green/30 transition-colors">
                  Kopier lenke
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
